import React from 'react';
import { Loader2, History, X } from 'lucide-react';
import { formatMoney, formatTimestamp } from '../../utils/annotationHelpers';

// Annotation history modal for a single lot. Presentational only — data
// fetching and open/close state live in AnnotationPage.
const AnnotationHistoryModal = ({
  historyLot,
  historyEntries,
  historyLoading,
  historyError,
  onClose,
}) => {
  if (!historyLot) return null;
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      onClick={onClose}
    >
      <div
        className="bg-surface rounded-lg shadow-xl w-full max-w-3xl max-h-[85vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-hairline px-4 py-3">
          <div className="flex items-center gap-2">
            <History size={16} className="text-muted" />
            <h2 className="text-base font-semibold text-ink">
              Annotation history — Lot {historyLot}
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="inline-flex items-center justify-center rounded p-1 text-muted hover:bg-surface-2"
            title="Close"
            aria-label="Close history"
          >
            <X size={16} />
          </button>
        </div>
        <div className="overflow-auto px-4 py-3">
          {historyLoading && (
            <div className="py-6 text-center text-muted">
              <Loader2 size={16} className="inline animate-spin mr-2" />
              Loading history…
            </div>
          )}
          {!historyLoading && historyError && (
            <div className="py-3 text-sm text-red-600 dark:text-red-400">{historyError}</div>
          )}
          {!historyLoading && !historyError && historyEntries.length === 0 && (
            <div className="py-6 text-center text-muted text-sm">
              No history recorded for this lot yet.
            </div>
          )}
          {!historyLoading && !historyError && historyEntries.length > 0 && (
            <table className="w-full text-left border-collapse text-sm">
              <thead>
                <tr className="bg-surface-2 border-b border-hairline">
                  <th className="p-2 font-semibold text-muted">When</th>
                  <th className="p-2 font-semibold text-muted">Action</th>
                  <th className="p-2 font-semibold text-muted text-right">Previous</th>
                  <th className="p-2 font-semibold text-muted text-right">New</th>
                  <th className="p-2 font-semibold text-muted">By</th>
                </tr>
              </thead>
              <tbody>
                {historyEntries.map((entry) => (
                  <tr
                    key={entry.id}
                    className={`border-b border-hairline last:border-b-0 ${
                      entry.synthetic ? 'bg-amber-500/10 italic text-muted' : ''
                    }`}
                    title={entry.synthetic ? 'This initial value predates history tracking, so the original author is unknown.' : undefined}
                  >
                    <td className="p-2 whitespace-nowrap">
                      {entry.synthetic ? 'before history tracking' : formatTimestamp(entry.created_at)}
                    </td>
                    <td className="p-2">
                      <span
                        className={`inline-block rounded px-2 py-0.5 text-xs font-semibold ${
                          entry.synthetic
                            ? 'bg-amber-500/10 text-amber-700 dark:text-amber-400 border border-amber-500/30'
                            : entry.action === 'create'
                            ? 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border border-emerald-500/30'
                            : entry.action === 'update'
                            ? 'bg-brand/10 text-brand border border-brand/20'
                            : 'bg-surface-2 text-muted border border-hairline'
                        }`}
                      >
                        {entry.synthetic ? 'create (untracked)' : (entry.action || '—')}
                      </span>
                    </td>
                    <td className="p-2 text-right font-mono">
                      {formatMoney(entry.previous_refactored_actual)}
                    </td>
                    <td className={`p-2 text-right font-mono ${entry.synthetic ? '' : 'font-semibold text-ink'}`}>
                      {formatMoney(entry.refactored_actual)}
                    </td>
                    <td className="p-2">
                      {entry.synthetic
                        ? 'unknown (pre-history)'
                        : (entry.username || (entry.user_id ? `#${entry.user_id}` : '—'))}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
};

export default AnnotationHistoryModal;
